/**
 * Trigger a browser save dialog for a remote file URL.
 *
 * The HTML `download` attribute is ignored for cross-origin links (S3
 * presigned URLs in our case) — the browser silently falls back to
 * `target="_blank"` and just opens the file inline. To force a real
 * download we fetch the bytes ourselves, hand the browser a same-origin
 * `blob:` URL, and click an anchor with `download` on it.
 *
 * Loads the whole file into memory. Fine for typical KB / VFS files; if
 * we ever serve multi-GB blobs we'll need a streaming-download story.
 */
export async function downloadFileFromUrl(url: string, filename: string): Promise<void> {
  const response = await fetch(url, { credentials: 'omit' });
  if (!response.ok) {
    throw new Error(`Download failed (${response.status})`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Defer revocation a tick so the click has time to register on
    // slower mobile Safari builds.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }
}
